import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Zap, Pencil, Trash2, ChevronRight, Sparkles, Mail, User, Tag, FileText, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const FIELDS = [
  { key: "from", label: "Mittente email", icon: Mail, hint: "es. farmacia@..." },
  { key: "subject", label: "Oggetto email", icon: Mail, hint: "es. ricevuta fiscale" },
  { key: "vendor", label: "Esercente rilevato", icon: User, hint: "es. Esselunga" },
  { key: "content", label: "Testo documento", icon: FileText, hint: "parola contenuta nell'OCR" },
  { key: "filename", label: "Nome file", icon: Tag, hint: "es. mutuo" },
];

const TAX_OPTS = [
  { key: "", label: "— nessuna —" },
  { key: "isee", label: "ISEE" },
  { key: "730_cu", label: "730 / CU" },
  { key: "both", label: "ISEE + 730/CU" },
  { key: "none", label: "Nessuna rilevanza fiscale" },
];

const emptyRule = () => ({
  name: "",
  conditions: [{ field: "from", value: "" }],
  action: { folder_id: "", tax_relevance: "" },
  priority: 100,
  enabled: true,
});

export default function RulesPage() {
  const [rules, setRules] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, f] = await Promise.all([api.get("/rules"), api.get("/folders")]);
      setRules(r.data);
      setFolders(f.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(emptyRule()); setOpen(true); };
  const openEdit = (r) => {
    setEditing({
      id: r.id,
      name: r.name,
      conditions: r.conditions.length ? r.conditions : [{ field: "from", value: "" }],
      action: { folder_id: r.action?.folder_id || "", tax_relevance: r.action?.tax_relevance || "" },
      priority: r.priority || 100,
      enabled: r.enabled !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Dai un nome alla regola"); return; }
    const conds = editing.conditions.filter(c => c.value.trim());
    if (!conds.length) { toast.error("Aggiungi almeno una condizione"); return; }
    if (!editing.action.folder_id && !editing.action.tax_relevance) {
      toast.error("Scegli almeno un'azione (cartella o rilevanza fiscale)"); return;
    }
    const body = {
      name: editing.name.trim(),
      conditions: conds,
      action: {
        ...(editing.action.folder_id ? { folder_id: editing.action.folder_id } : {}),
        ...(editing.action.tax_relevance ? { tax_relevance: editing.action.tax_relevance } : {}),
      },
      priority: Number(editing.priority) || 100,
      enabled: editing.enabled,
    };
    try {
      if (editing.id) {
        await api.patch(`/rules/${editing.id}`, body);
      } else {
        await api.post("/rules", body);
      }
      toast.success(editing.id ? "Regola aggiornata" : "Regola creata");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la regola "${r.name}"?`)) return;
    await api.delete(`/rules/${r.id}`);
    toast.success("Regola eliminata");
    load();
  };

  const toggle = async (r, enabled) => {
    await api.patch(`/rules/${r.id}`, { enabled });
    load();
  };

  const addCondition = () => setEditing(e => ({ ...e, conditions: [...e.conditions, { field: "content", value: "" }] }));
  const removeCondition = (idx) => setEditing(e => ({ ...e, conditions: e.conditions.filter((_, i) => i !== idx) }));
  const updateCondition = (idx, patch) => setEditing(e => ({
    ...e,
    conditions: e.conditions.map((c, i) => i === idx ? { ...c, ...patch } : c),
  }));

  const fieldLabel = (k) => FIELDS.find(f => f.key === k)?.label || k;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline text-[#0A0A0A] mb-3">— AUTOMAZIONE</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">Regole personalizzate</h1>
          <p className="text-sm text-[#5C5C5C] max-w-xl">
            Crea regole che l'app applica automaticamente ad ogni documento importato:
            "se mittente contiene <b>farmacia</b> → cartella <b>730/CU</b>".
          </p>
        </div>
        <Button
          data-testid="new-rule-btn"
          onClick={openNew}
          className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-10"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuova regola
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-[#8A8A8A]">Caricamento…</div>
      ) : rules.length === 0 ? (
        <div className="border border-dashed border-black/15 rounded-xl bg-white p-16 text-center">
          <div className="w-14 h-14 mx-auto bg-[#0A0A0A]/5 flex items-center justify-center rounded-xl mb-6">
            <Zap className="w-6 h-6 text-[#0A0A0A]" strokeWidth={1.5} />
          </div>
          <h3 className="font-display font-bold text-2xl mb-2 tracking-tight">Nessuna regola ancora</h3>
          <p className="text-sm text-[#5C5C5C] mb-6 max-w-md mx-auto">
            Crea la prima regola per automatizzare l'archiviazione. Esempio: tutte le email da
            "farmacia" vanno automaticamente nella sezione 730/CU.
          </p>
          <Button onClick={openNew} data-testid="empty-new-rule-btn" className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white">
            <Plus className="w-4 h-4 mr-2" /> Crea la prima regola
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-black/10 rounded-xl overflow-hidden">
          {rules.map((r, i) => (
            <div key={r.id} data-testid={`rule-${r.id}`} className={`px-6 py-5 flex items-start gap-4 ${i < rules.length - 1 ? "border-b border-black/5" : ""}`}>
              <Switch
                data-testid={`rule-toggle-${r.id}`}
                checked={r.enabled !== false}
                onCheckedChange={(v) => toggle(r, v)}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-display font-bold text-lg tracking-tight">{r.name}</h3>
                  <span className="overline font-mono-plex text-[#8A8A8A]">P{r.priority}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-[#5C5C5C]">
                  <span className="text-[#8A8A8A]">SE</span>
                  {r.conditions.map((c, ci) => (
                    <span key={ci} className="inline-flex items-center gap-1">
                      {ci > 0 && <span className="text-[#8A8A8A]">E</span>}
                      <span className="bg-black/5 rounded-lg px-2 py-1">
                        <b>{fieldLabel(c.field)}</b> contiene "<b>{c.value}</b>"
                      </span>
                    </span>
                  ))}
                  <ArrowRight className="w-3.5 h-3.5 mx-1 text-[#8A8A8A]" />
                  <span className="text-[#8A8A8A]">ALLORA</span>
                  {r.action_folder_name && (
                    <span className="bg-[#0A0A0A] text-white rounded-lg px-2 py-1">
                      Cartella <b>{r.action_folder_name}</b>
                    </span>
                  )}
                  {r.action?.tax_relevance && (
                    <span className="bg-[#009966] text-white rounded-lg px-2 py-1 inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> {r.action.tax_relevance === "isee" ? "ISEE" : r.action.tax_relevance === "730_cu" ? "730 / CU" : r.action.tax_relevance === "both" ? "ISEE + 730" : "Nessuna rilev."}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  data-testid={`rule-edit-${r.id}`}
                  onClick={() => openEdit(r)}
                  className="p-2 hover:bg-black/5 rounded-lg text-[#5C5C5C]"
                  title="Modifica"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`rule-delete-${r.id}`}
                  onClick={() => remove(r)}
                  className="p-2 hover:bg-[#FF3333]/5 rounded-lg text-[#5C5C5C] hover:text-[#FF3333]"
                  title="Elimina"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight text-2xl">
              {editing?.id ? "Modifica regola" : "Nuova regola"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#5C5C5C]">
              Definisci condizioni (SE) e azione (ALLORA) che l'app applica in automatico.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-5 py-2 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <Label className="text-xs uppercase tracking-widest">Nome regola</Label>
                <Input
                  data-testid="rule-name-input"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Es. Ricevute farmacia → 730/CU"
                  className="rounded-xl mt-2 h-11 bg-white focus-visible:ring-[#0A0A0A]"
                />
              </div>

              {/* Conditions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs uppercase tracking-widest">SE — Condizioni</Label>
                  <button
                    data-testid="add-condition-btn"
                    type="button"
                    onClick={addCondition}
                    className="text-xs text-[#0A0A0A] hover:underline flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Aggiungi condizione
                  </button>
                </div>
                <div className="space-y-2">
                  {editing.conditions.map((c, i) => {
                    const fld = FIELDS.find(f => f.key === c.field);
                    return (
                      <div key={i} className="flex gap-2 items-start bg-[#F8F8F6] border border-black/10 rounded-xl p-3">
                        <Select value={c.field} onValueChange={(v) => updateCondition(i, { field: v })}>
                          <SelectTrigger data-testid={`cond-field-${i}`} className="w-48 rounded-xl bg-white h-10 border-black/15">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FIELDS.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="flex-1">
                          <div className="text-[10px] text-[#8A8A8A] font-mono-plex mb-1">contiene</div>
                          <Input
                            data-testid={`cond-value-${i}`}
                            value={c.value}
                            onChange={(e) => updateCondition(i, { value: e.target.value })}
                            placeholder={fld?.hint}
                            className="rounded-xl bg-white h-10 focus-visible:ring-[#0A0A0A]"
                          />
                        </div>
                        {editing.conditions.length > 1 && (
                          <button
                            data-testid={`cond-remove-${i}`}
                            type="button"
                            onClick={() => removeCondition(i)}
                            className="p-2 hover:bg-black/5 rounded-lg text-[#5C5C5C] mt-4"
                            title="Rimuovi"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Action */}
              <div>
                <Label className="text-xs uppercase tracking-widest">ALLORA — Azione</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <div>
                    <div className="text-[10px] text-[#8A8A8A] font-mono-plex mb-1">Cartella di destinazione</div>
                    <Select
                      value={editing.action.folder_id || "none"}
                      onValueChange={(v) => setEditing({ ...editing, action: { ...editing.action, folder_id: v === "none" ? "" : v } })}
                    >
                      <SelectTrigger data-testid="action-folder-select" className="rounded-xl bg-white h-10 border-black/15">
                        <SelectValue placeholder="— nessuna —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— nessuna —</SelectItem>
                        {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#8A8A8A] font-mono-plex mb-1">Rilevanza fiscale</div>
                    <Select
                      value={editing.action.tax_relevance || "none"}
                      onValueChange={(v) => setEditing({ ...editing, action: { ...editing.action, tax_relevance: v === "none" ? "" : v } })}
                    >
                      <SelectTrigger data-testid="action-tax-select" className="rounded-xl bg-white h-10 border-black/15">
                        <SelectValue placeholder="— nessuna —" />
                      </SelectTrigger>
                      <SelectContent>
                        {TAX_OPTS.map(o => (
                          <SelectItem key={o.key || "none"} value={o.key || "none"}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Priorità</Label>
                  <Input
                    data-testid="rule-priority-input"
                    type="number"
                    value={editing.priority}
                    onChange={(e) => setEditing({ ...editing, priority: e.target.value })}
                    className="rounded-xl mt-2 h-10 bg-white"
                  />
                  <div className="text-[10px] text-[#8A8A8A] mt-1">Numeri più bassi vengono valutati prima.</div>
                </div>
                <div className="flex items-end pb-1">
                  <div className="flex items-center gap-3 bg-[#F8F8F6] border border-black/10 rounded-xl p-3 w-full">
                    <Switch
                      data-testid="rule-enabled-toggle"
                      checked={editing.enabled}
                      onCheckedChange={(v) => setEditing({ ...editing, enabled: v })}
                    />
                    <div>
                      <div className="text-sm font-medium">Regola attiva</div>
                      <div className="text-[10px] text-[#8A8A8A]">Applicata ai nuovi documenti</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl border-black/20">Annulla</Button>
            <Button data-testid="rule-save-btn" onClick={save} className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white">
              Salva regola
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
